import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  Banner,
  Button,
  FieldText,
  FieldTextarea,
  Modal,
} from "@knowtex-ai/components";
import { ArrowDown, ArrowUp, PasteClipboard, Plus, Trash } from "iconoir-react";
import { playbookFromText, type ParsedPlaybook } from "./playbookText";
import RowActions from "./RowActions";

export const MAX_CHECKS = 200;
export const MAX_CHECK_CHARS = 2000;
export const MAX_NOTES_CHARS = 4000;

/** A check being edited. `id` is unset until mc-api saves it. */
export interface DraftCheck {
  key: string;
  id?: string;
  text: string;
  notes: string;
  showNotes: boolean;
}

let lastKey = 0;
export const draftCheck = (c?: {
  id?: string;
  text?: string;
  notes?: string;
}): DraftCheck => ({
  key: `draft-${++lastKey}`,
  id: c?.id,
  text: c?.text ?? "",
  notes: c?.notes ?? "",
  showNotes: !!c?.notes,
});

type Focus = { key: string; field: "text" | "notes" };

export default function PlaybookChecksField({
  checks,
  onChange,
  onImport,
  disabled,
}: {
  checks: DraftCheck[];
  onChange: (checks: DraftCheck[]) => void;
  onImport: () => void;
  disabled?: boolean;
}) {
  const labelId = useId();
  const fields = useRef(new Map<string, HTMLElement>());
  const [focus, setFocus] = useState<Focus | null>(null);
  useEffect(() => {
    if (!focus) return;
    fields.current.get(`${focus.field}:${focus.key}`)?.focus();
    setFocus(null);
  }, [focus]);
  const ref =
    (field: Focus["field"], key: string) => (el: HTMLElement | null) => {
      if (el) fields.current.set(`${field}:${key}`, el);
      else fields.current.delete(`${field}:${key}`);
    };

  const full = checks.length >= MAX_CHECKS;
  const patch = (key: string, change: Partial<DraftCheck>) =>
    onChange(checks.map((c) => (c.key === key ? { ...c, ...change } : c)));
  const insertAfter = (index: number) => {
    if (full) return;
    const added = draftCheck();
    onChange([
      ...checks.slice(0, index + 1),
      added,
      ...checks.slice(index + 1),
    ]);
    setFocus({ key: added.key, field: "text" });
  };
  /** Focuses the row that takes its place, or the one before when `focusPrevious`. */
  const remove = (index: number, focusPrevious = false) => {
    const next = checks.filter((_, i) => i !== index);
    onChange(next);
    const neighbor =
      next[
        focusPrevious
          ? Math.max(index - 1, 0)
          : Math.min(index, next.length - 1)
      ];
    if (neighbor) setFocus({ key: neighbor.key, field: "text" });
  };
  const move = (index: number, by: -1 | 1) => {
    const next = [...checks];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    onChange(next);
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const check = checks[index];
    if (event.target !== fields.current.get(`text:${check.key}`)) return;
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Enter") {
      event.preventDefault();
      insertAfter(index);
    } else if (
      event.key === "Backspace" &&
      !check.text &&
      !check.notes &&
      checks.length > 1
    ) {
      event.preventDefault();
      remove(index, true);
    }
  };

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span id={labelId} className="text-[13px] text-gray-700">
          Checks
        </span>
        <p className="m-0 text-xs leading-[1.4] text-gray-500">
          Add one thing to test per row, in order. Press Enter to start the next
          one.
        </p>
      </div>
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {checks.map((check, index) => {
          const n = index + 1;
          return (
            <li
              key={check.key}
              className="flex items-start gap-2"
              onKeyDown={(event) => onKeyDown(event, index)}
            >
              <span
                className="w-6 shrink-0 pt-[7px] text-right text-[13px] tabular-nums text-gray-500"
                aria-hidden="true"
              >
                {n}.
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <FieldText
                  ref={ref("text", check.key)}
                  aria-label={`Check ${n}`}
                  placeholder={
                    index === 0 ? "e.g. Sign in as a provider" : undefined
                  }
                  value={check.text}
                  onChange={(e) =>
                    patch(check.key, {
                      text: e.target.value.slice(0, MAX_CHECK_CHARS),
                    })
                  }
                  maxLength={MAX_CHECK_CHARS}
                  autoComplete="off"
                  size="small"
                  disabled={disabled}
                />
                {check.showNotes ? (
                  <FieldTextarea
                    ref={ref("notes", check.key)}
                    aria-label={`Note for check ${n}`}
                    placeholder="Only what MC can't work out: expected messages or timings, flags, roles, test data, when to skip"
                    value={check.notes}
                    onChange={(e) =>
                      patch(check.key, {
                        notes: e.target.value.slice(0, MAX_NOTES_CHARS),
                      })
                    }
                    onBlur={() =>
                      !check.notes.trim() &&
                      patch(check.key, { notes: "", showNotes: false })
                    }
                    maxLength={MAX_NOTES_CHARS}
                    autoComplete="off"
                    rows={2}
                    resize="vertical"
                    size="small"
                    disabled={disabled}
                  />
                ) : (
                  <div>
                    <Button
                      variant="text"
                      size="small"
                      icon={Plus}
                      aria-label={`Add an optional note to check ${n}`}
                      onClick={() => {
                        patch(check.key, { showNotes: true });
                        setFocus({ key: check.key, field: "notes" });
                      }}
                      disabled={disabled}
                    >
                      Add optional note
                    </Button>
                  </div>
                )}
              </div>
              <div className="shrink-0">
                <RowActions
                  label={`Actions for check ${n}`}
                  actions={[
                    {
                      key: "up",
                      label: "Move up",
                      icon: ArrowUp,
                      onClick: () => move(index, -1),
                      disabled: disabled || index === 0,
                    },
                    {
                      key: "down",
                      label: "Move down",
                      icon: ArrowDown,
                      onClick: () => move(index, 1),
                      disabled: disabled || index === checks.length - 1,
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      icon: Trash,
                      danger: true,
                      onClick: () => remove(index),
                      disabled: disabled || checks.length === 1,
                    },
                  ]}
                />
              </div>
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-2 pl-8">
        <Button
          variant="secondary"
          size="small"
          icon={Plus}
          onClick={() => insertAfter(checks.length - 1)}
          disabled={disabled || full}
        >
          Add check
        </Button>
        <Button
          variant="text"
          size="small"
          icon={PasteClipboard}
          onClick={onImport}
          disabled={disabled || full}
        >
          Import from text
        </Button>
      </div>
    </div>
  );
}

const MAX_IMPORT_CHARS = 50_000;

/** Turns pasted text (an old playbook, a doc, a list) into checks. */
export function ImportChecksModal({
  open,
  onClose,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  onImport: (parsed: ParsedPlaybook) => void;
}) {
  const [text, setText] = useState("");
  const [empty, setEmpty] = useState(false);
  const close = () => {
    setEmpty(false);
    onClose();
  };
  const generate = () => {
    const parsed = playbookFromText(text);
    if (!parsed.checks.length) return setEmpty(true);
    onImport(parsed);
    setText("");
    close();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close()}
      title="Import from text"
      size="medium"
    >
      <div className="flex flex-col gap-5">
        <FieldTextarea
          label="Text"
          description="Each bullet becomes a check, and lines under it become its notes. Text before the list becomes the description. Without bullets, each line is a check."
          value={text}
          onChange={(e) => {
            setText(e.target.value.slice(0, MAX_IMPORT_CHARS));
            setEmpty(false);
          }}
          maxLength={MAX_IMPORT_CHARS}
          autoComplete="off"
          autoFocus
          rows={12}
          resize="vertical"
        />
        {empty && (
          <Banner variant="warning" heading="No checks found">
            Nothing in that text looks like a check.
          </Banner>
        )}
        <p className="m-0 text-xs leading-[1.4] text-gray-500">
          Checks are added after the ones you already have.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="button" onClick={generate} disabled={!text.trim()}>
            Generate checks
          </Button>
        </div>
      </div>
    </Modal>
  );
}
